import type { GlowGetApi, PaginatedResponse } from "@glow/shared";
import uniqBy from "lodash/uniqBy";
import { useCallback, useEffect, useReducer, useRef } from "react";
import { useBoolean } from "@lux/atoms/hooks/useBoolean";
import { useStableObject } from "@lux/atoms/hooks/useStableObject";
import { GlowApiClient } from "./GlowClientConstructor";
import { useLoadMoreObserver } from "@lux/atoms/hooks/useLoadMoreObserver";

type UsePaginatedRequestState<Entries> = {
  status: "initial-load" | "no-data" | "has-data" | "error";
  entries: Entries;
  has_more: boolean;
  next_cursor: string | undefined;
};

type PaginationAction<Entry extends { api_id: string }> =
  | { type: "reset" }
  | ({
      type: "received-first-page";
    } & PaginatedResponse<Entry>)
  | {
      type: "local-update";
      entries: Entry[];
    }
  | ({
      type: "received-next";
    } & PaginatedResponse<Entry>)
  | { type: "error" };

type Reducer<Entry extends { api_id: string }> = (
  state: UsePaginatedRequestState<Entry[]>,
  action: PaginationAction<Entry>,
) => UsePaginatedRequestState<Entry[]>;

// We don't want to use `randomString` from @glow/shared
// because then Jest would need to be able to parse JSX, which would
// require configuring Babel etc.
const randomString = () => Math.random().toString();

const paginationReducer: Reducer<any> = (state, action) => {
  switch (action.type) {
    case "reset": {
      return {
        status: "initial-load",
        entries: [],
        next_cursor: undefined,
        has_more: true,
      };
    }
    case "received-first-page": {
      const existingEntries = state.entries;
      if (existingEntries.length === 0) {
        return {
          status: "has-data",
          entries: action.entries,
          has_more: action.has_more,
          next_cursor: action.next_cursor,
        };
      }

      const newEntryIds = new Set(action.entries.map((entry) => entry.api_id));
      const hasOverlap = state.entries.some((entry) =>
        newEntryIds.has(entry.api_id),
      );

      // If there is no overlap, we will discard the existing entries and state.
      if (!hasOverlap) {
        return {
          status: "has-data",
          entries: action.entries,
          has_more: action.has_more,
          next_cursor: action.next_cursor,
        };
      }

      // If there is overlap, we will do uniqBy to remove the overlapping elements. `uniqBy`
      // should keep the new version returned from the server rather than the version in state.
      // Note: This rests on the assumption that the sort order of the entries is consistent.
      const entries = uniqBy([...action.entries, ...state.entries], "api_id");
      return {
        status: "has-data",
        entries,
        has_more: state.has_more,
        next_cursor: state.next_cursor,
      };
    }
    case "received-next": {
      return {
        status: "has-data",
        entries: [...state.entries, ...action.entries],
        has_more: action.has_more,
        next_cursor: action.next_cursor,
      };
    }
    case "local-update": {
      return {
        status: "has-data",
        entries: action.entries,
        has_more: state.has_more,
        next_cursor: state.next_cursor,
      };
    }
    case "error": {
      return {
        ...state,
        status: "error",
      };
    }
  }
};

export type PaginatedRequestState<Entries> = {
  hasMore: boolean;
  loadMore: () => void;
  fetchFirstPage: () => void;
  reset: () => void;
  entries: Entries;
  updateEntries: (transform: (entries: Entries) => Entries) => void;
  fetching: boolean;
  status: "initial-load" | "no-data" | "has-data" | "error";
  loadMoreDiv: React.ReactNode;
};

/**
 * This hook allows us to fetch paginated data. This is suitable for infinite scrolling
 * and pairs well with `useLoadMoreObserver` which will trigger `loadMore` when the page
 * has been scrolled.
 *
 * Note: this hook requires a unique `api_id` to exist on every entry. We use the `api_id` to
 * de-dupe results across requests.
 *
 * ```
 *             │
 *      Change │ Initial
 *      Query  │ Load
 *             │
 *             ▼
 *      ┌────────────┐
 *      │   Status   │
 *   ┌──┤initial-load│
 *   │  └──────┬─────┘
 *   │         │  no data found
 *   │         ▼
 *   │  ┌────────────┐
 *   │  │   Status   │
 *   │  │  no-data   │
 *   │  └────────────┘
 *   │
 *   │fetched initial data
 *   └─────────┐
 *             ▼
 *      ┌────────────┐
 *      │   Status   │
 *   ┌──┤  has-data  │
 *   │  └────────────┘
 *   │       ▲
 *   └───────┘
 * fetch-next-page
 * ```
 *
 * This has some advantages over `useSWRInfinite` which is what we were using before:
 * - we can refresh the first page of data
 * - we store entries in one large list rather than separating them into pages
 * - simpler + easier to understand and change
 * - we usually don't want caching for paginated data
 *
 * This has some disadvantages:
 * - `useSWRInfinite` allows us to refresh without removing the data, this doesn't allow that
 *
 * The hook can also enter "error" state. This means that `loadMore` failed
 * to load more entries. Hook doesn't switch to "error" state if `fetchFirstPage` fails.
 * Since `loadMore` is called when hook is initialized, failure to load
 * first page of data moves hook to "error" state too.
 */
export const createUsePaginatedRequest = (zmClient: GlowApiClient) => {
  const usePaginatedRequest = <
    Path extends keyof GlowGetApi,
    Entry extends { api_id: string },
    Request extends GlowGetApi[Path]["request"],
    Response extends GlowGetApi[Path]["response"] & PaginatedResponse<Entry>,
  >({
    path,
    args: _args,
    pageSize = 100,
    pause,
  }: {
    path: Path;
    args?: Request;
    pageSize?: number;
    pause?: boolean;
  }): PaginatedRequestState<
    GlowGetApi[Path]["response"] extends PaginatedResponse<Entry>
      ? GlowGetApi[Path]["response"]["entries"]
      : null
  > => {
    const [state, dispatch] = useReducer(
      paginationReducer as Reducer<Response["entries"][number]>,
      {
        status: "initial-load",
        entries: [],
        next_cursor: undefined,
        has_more: true,
      },
    );

    // We want to set `fetching` to true immediately if we are going to make a request. If we don't
    // do that, there will be a brief moment where `status = initial-load` and `fetching = false`
    const {
      value: fetching,
      setTrue: onFetchStart,
      setFalse: onFetchEnd,
      setValue: setFetching,
    } = useBoolean(!pause);

    /**
     * We store the current fetch IDs as refs to prevent calling the same fetch more than once.
     * If we are fetching the next page of data, we want to prevent calling that function again
     * since it will not improve performance and it could lead to race conditions.
     *
     * We use `useRef` instead of `useState` because:
     * 1. we don't need the component to re-render when we set this
     * 2. we want to set this instantly and not wait for another `setState` lifecycle
     */
    const fetchNextId = useRef<string | null>(null);
    const fetchFirstId = useRef<string | null>(null);

    // We only want to re-run the hooks below if the `args` meaningfully changes. Callers of this
    // function will often construct the `args` object on the fly which means that the object
    // identity may change with every render. We use `JSON.stringify` to compare the value of `args`
    // and only re-run the hooks if that changes.
    const args = useStableObject(_args);

    const fetchNext = useCallback(async () => {
      if (pause || fetchNextId.current || !state.has_more) {
        return;
      }

      fetchNextId.current = randomString();
      onFetchStart();

      try {
        const data = (await zmClient.get(path, {
          ...args,
          pagination_cursor: state.next_cursor,
          pagination_limit: pageSize,
        })) as PaginatedResponse<Entry>;

        dispatch({
          type: "received-next",
          ...data,
        });
      } catch (error) {
        dispatch({ type: "error" });
      } finally {
        fetchNextId.current = null;
        onFetchEnd();
      }
    }, [
      pause,
      onFetchStart,
      onFetchEnd,
      pageSize,
      path,
      state.next_cursor,
      state.has_more,
      args,
    ]);

    useEffect(() => {
      // This is the hook that loads data when the component is loaded or when the component is
      // reset.
      if (state.status === "initial-load") {
        fetchNext();
      }
    }, [fetchNext, state.status]);

    const reset = useCallback(() => {
      // Reset internal state
      dispatch({ type: "reset" });
      // Update fetching to whether we _are going to_
      // make a request. If we don't do that there's
      // a brief period with `state = initial-load`
      // and `fetching = false`. See `fetching` above.
      setFetching(!pause);

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    // Reset hook state if the path or args change
    useEffect(() => {
      reset();
    }, [path, args, reset]);

    const fetchFirstPage = useCallback(async () => {
      if (pause || fetchFirstId.current) {
        return;
      }

      // We don't call `fetching.setTrue` because that indicates that we are fetching the *next*
      // page of data.
      fetchFirstId.current = randomString();

      try {
        const data = (await zmClient.get(path, {
          ...args,
          pagination_cursor: undefined,
          pagination_limit: pageSize,
        })) as PaginatedResponse<Entry>;

        dispatch({
          type: "received-first-page",
          ...data,
        });
      } finally {
        fetchFirstId.current = null;
      }
    }, [dispatch, pageSize, path, pause, args]);

    const updateEntries = useCallback(
      (transform: (entries: Entry[]) => Entry[]) => {
        if (state.entries) {
          const transformed = transform(state.entries);
          dispatch({ type: "local-update", entries: transformed });
        }
        return;
      },
      [state],
    );

    const loadMoreDiv = useLoadMoreObserver({
      loadMore: fetchNext,
      depArray: [state.entries.length],
    });

    return {
      status:
        state.status === "has-data" && state.entries.length === 0
          ? "no-data"
          : state.status,
      entries: state.entries as any,
      fetching,
      fetchFirstPage,
      reset,
      loadMore: fetchNext,
      updateEntries: updateEntries as any,
      hasMore: state.has_more,
      loadMoreDiv,
    };
  };

  return usePaginatedRequest;
};
